import { ApiError } from '@/lib/api-client';

/**
 * S5 — traducir el rechazo de `claim-inbound` a algo que la screener pueda
 * hacer, con el miembro esperando en la línea.
 *
 * El backend contesta con un `code` estable; el `message` crudo del server
 * está en inglés y no dice qué hacer. Nest envuelve el objeto de la excepción
 * dentro de `message`, pero algunos caminos lo dejan arriba — se prueban los
 * dos, como ya hace `extractMessage` en el cliente de API.
 */
export type InboundClaimErrorKey =
  | 'clientProfile.claimInbound.alreadyAssigned'
  | 'clientProfile.claimInbound.ineligible'
  | 'clientProfile.claimInbound.notEligible'
  | 'clientProfile.claimInbound.notDropped'
  | 'clientProfile.claimInbound.phoneMismatch'
  | 'clientProfile.claimInbound.dailyCap';

export function inboundClaimErrorKey(err: unknown): InboundClaimErrorKey | null {
  const code = inboundClaimErrorCode(err);
  switch (code) {
    case 'CLIENT_ALREADY_ASSIGNED':
      return 'clientProfile.claimInbound.alreadyAssigned';
    case 'CLIENT_INELIGIBLE':
      return 'clientProfile.claimInbound.ineligible';
    case 'CLIENT_NOT_ELIGIBLE_FOR_QUEUE':
      return 'clientProfile.claimInbound.notEligible';
    case 'NOT_A_DROPPED_CALL':
      return 'clientProfile.claimInbound.notDropped';
    // H5 — los dos rechazos nuevos. El crudo del server es útil para el log y
    // no para la screener: «phone does not match member record» no dice qué
    // hacer con el miembro esperando.
    case 'INBOUND_PHONE_MISMATCH':
      return 'clientProfile.claimInbound.phoneMismatch';
    case 'INBOUND_DAILY_CAP_REACHED':
      return 'clientProfile.claimInbound.dailyCap';
    default:
      // Sin código legible, la pantalla muestra el crudo del server. Es feo,
      // pero es información: tragárselo dejaría a la screener sin nada.
      return null;
  }
}

/** El `code` del rechazo, mire donde mire Nest. `null` si no hay ninguno. */
export function inboundClaimErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const payload = err.payload as { code?: unknown; message?: unknown } | null;
  if (!payload) return null;
  if (typeof payload.code === 'string' && payload.code.length > 0) return payload.code;
  const inner = payload.message as { code?: unknown } | null | undefined;
  if (inner && typeof inner === 'object' && typeof inner.code === 'string' && inner.code.length > 0) {
    return inner.code;
  }
  return null;
}
